import { Context } from '@/context/appContext';
import { getResponseError } from '@/helpers';
import { useNotifications } from '@/hooks/useNotifications';
import { AppStateActions } from '@/reducers/appReducer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { createUserAndLogin, generateToken } from '@editar-pdf/api';
import { useFormik } from 'formik';
import { Mail } from 'lucide-react';
import { useCallback, useContext, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LabeledField } from '@/components/LabeledField';
import { fireSignupConversion, logEvent } from '@/helpers/eventLogger';

type LoginFormDataType = {
  email: string;
  password: string;
};

function EmailForm() {
  const { t } = useTranslation('web', { keyPrefix: 'components.AuthModal' });
  const {
    state: { authState },
    dispatch,
  } = useContext(Context);

  const { notifyError } = useNotifications();
  const [loading, setLoading] = useState(false);
  const emailId = useId();
  const passwordId = useId();

  const handleLogin = useCallback(
    async (data: LoginFormDataType) => {
      logEvent({ logType: 'EVENT', eventType: 'USER_SIGN_IN' });
      setLoading(true);
      try {
        const resp = await generateToken(data);
        dispatch({ type: AppStateActions.SET_AUTH_TOKEN, payload: { token: resp.data, source: 'SIGN_IN' } });
        dispatch({ type: AppStateActions.SET_AUTH_STATE, payload: {} });
      } catch (e: unknown) {
        logEvent({ logType: 'ERROR', eventType: 'USER_SIGN_IN_ERROR', trace: e });
        notifyError(getResponseError(e));
      } finally {
        setLoading(false);
      }
    },
    [dispatch, notifyError],
  );

  const handleSendEmail = useCallback(
    async (data: LoginFormDataType) => {
      logEvent({ logType: 'EVENT', eventType: 'USER_SIGN_UP' });
      fireSignupConversion();
      setLoading(true);
      try {
        const resp = await createUserAndLogin(data);
        dispatch({ type: AppStateActions.SET_AUTH_TOKEN, payload: { token: resp.data, source: 'SIGN_UP' } });
        dispatch({ type: AppStateActions.SET_AUTH_STATE, payload: {} });
      } catch (e: unknown) {
        logEvent({ logType: 'ERROR', eventType: 'USER_SIGN_UP_ERROR', trace: e });
        notifyError(getResponseError(e));
      } finally {
        setLoading(false);
      }
    },
    [dispatch, notifyError],
  );

  const handleRestore = useCallback(() => {
    dispatch({ type: AppStateActions.SET_AUTH_STATE, payload: { state: 'RESTORE' } });
  }, [dispatch]);

  // Two Formik instances rather than one with a swapping submit handler: sign-in and sign-up
  // keep their own values, so toggling between the two views does not carry a half-typed
  // password across. Only one of them is ever bound to the form below.
  const loginForm = useFormik<LoginFormDataType>({
    initialValues: { email: '', password: '' },
    onSubmit: handleLogin,
  });

  const emailForm = useFormik<LoginFormDataType>({
    initialValues: { email: '', password: '' },
    onSubmit: handleSendEmail,
  });

  const isSignIn = authState.state === 'SIGN_IN';
  const form = isSignIn ? loginForm : emailForm;

  return (
    <form onSubmit={form.handleSubmit}>
      <div className="flex flex-col gap-4">
        <LabeledField label={t('emailLabel')} htmlFor={emailId}>
          <Input
            id={emailId}
            required
            name="email"
            type="email"
            autoComplete="email"
            placeholder={t('emailPlaceholder')}
            value={form.values.email}
            onChange={form.handleChange}
          />
        </LabeledField>

        <LabeledField label={t('passwordLabel')} htmlFor={passwordId}>
          <Input
            id={passwordId}
            required
            name="password"
            type="password"
            autoComplete={isSignIn ? 'current-password' : 'new-password'}
            placeholder={t('passwordPlaceholder')}
            value={form.values.password}
            onChange={form.handleChange}
          />
          {isSignIn ? (
            // A real button, not the click-handling <Typography> this replaced: that one was
            // unreachable by keyboard.
            <button
              type="button"
              onClick={handleRestore}
              className="cursor-pointer self-start rounded-sm text-[12px] text-primary hover:underline focus-visible:ring-focus"
            >
              {t('forgotPassword')}
            </button>
          ) : (
            <p className="text-[12px] text-muted-foreground">{t('passwordInfo')}</p>
          )}
        </LabeledField>

        <Button type="submit" className="w-full" loading={loading}>
          {!loading && <Mail />}
          {isSignIn ? t('logInWithEmail') : t('continueWithEmail')}
        </Button>
      </div>
    </form>
  );
}

export { EmailForm };
